// covered-by: packages/schemas/src/christo/__tests__/approvalEvent.test.ts
// covered-by: services/christo/app/handlers/catalyst/__tests__/reduceParticipation.test.ts
import { z } from "zod";

import { reviewDecisionSchema } from "../commons/ticket";

/**
 * Append-only approval participation log. Source of truth for who was
 * suggested/invited, who recorded a position, and note attachments. UI/rollup
 * reads are projections rebuilt by reduceParticipation — never authoritative.
 *
 * ReviewDecision is a two-value enum ("approved" | "changes_requested");
 * "no position yet" is represented by the absence of a position_recorded event,
 * not a third enum literal.
 */
export const approvalEventTypeSchema = z.enum([
  "participant_suggested",
  "participant_invited",
  "invitation_declined",
  "position_recorded",
  "position_withdrawn",
  "note_added",
]);
export type ApprovalEventType = z.infer<typeof approvalEventTypeSchema>;

export const approvalActorKindSchema = z.enum([
  "user",
  "agent",
  "system",
  "integration",
]);
export type ApprovalActorKind = z.infer<typeof approvalActorKindSchema>;

export const approvalActorSchema = z.object({
  kind: approvalActorKindSchema,
  userId: z.string().nullable(),
  integration: z.enum(["wrike", "asana", "email"]).nullable(),
});
export type ApprovalActor = z.infer<typeof approvalActorSchema>;

export const approvalEventSourceSchema = z.enum([
  "app",
  "agent",
  "wrike",
  "asana",
  "email",
]);
export type ApprovalEventSource = z.infer<typeof approvalEventSourceSchema>;

export const approvalEventSchema = z.object({
  eventId: z.string(),
  enterpriseId: z.string(),
  projectId: z.string(),
  fileId: z.string(),
  versionId: z.string(),
  ticketId: z.string().nullable(),

  type: approvalEventTypeSchema,
  actor: approvalActorSchema,

  subjectUserId: z.string().nullable(),
  suggestedRole: z.string().nullable(),
  reasoning: z.string().nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  basis: z.array(z.string()).default([]),

  position: reviewDecisionSchema.nullable(),

  noteId: z.string().nullable(),

  source: approvalEventSourceSchema,
  supersedesEventId: z.string().nullable(),
  idempotencyKey: z.string().min(1),
  createdAt: z.coerce.date(),
});
export type ApprovalEvent = z.infer<typeof approvalEventSchema>;

/** Latest position per user (projection). */
export const participationPositionSchema = z.object({
  userId: z.string(),
  position: reviewDecisionSchema,
  at: z.coerce.date(),
  noteId: z.string().nullable(),
  source: z.string(),
});

export const participationInvitedSchema = z.object({
  userId: z.string(),
  at: z.coerce.date(),
  suggestedRole: z.string().nullable(),
  status: z.enum(["invited", "declined"]),
  fromSuggestion: z.boolean(),
});

export const participationSuggestedSchema = z.object({
  userId: z.string(),
  suggestedRole: z.string().nullable(),
  reasoning: z.string().nullable(),
  confidence: z.number().nullable(),
  at: z.coerce.date(),
});

export const participationSnapshotSchema = z.object({
  positions: z.array(participationPositionSchema),
  invited: z.array(participationInvitedSchema),
  suggested: z.array(participationSuggestedSchema),
  counts: z.object({
    approved: z.number(),
    changesRequested: z.number(),
    noPosition: z.number(),
  }),
  noteCount: z.number(),
  lastEventAt: z.coerce.date().nullable(),
  snapshotStamp: z.string(),
});
export type ParticipationSnapshot = z.infer<typeof participationSnapshotSchema>;

export const approvalRollupSignalSchema = z.enum([
  "no_activity",
  "awaiting_input",
  "in_discussion",
  "changes_requested",
  "looks_approved",
]);
export type ApprovalRollupSignal = z.infer<typeof approvalRollupSignalSchema>;

export const approvalRollupSchema = z.object({
  signal: approvalRollupSignalSchema,
  totalAssets: z.number(),
  assetsWithActivity: z.number(),
  assetsWithOpenConcerns: z.number(),
  assetsLookingApproved: z.number(),
  invalidationStamp: z.string(),
});
export type ApprovalRollup = z.infer<typeof approvalRollupSchema>;

/**
 * Append request body. Actor for human app/agent-as-user writes is always
 * taken from the JWT (req.userID) — never trusted from the body. Optional
 * actor fields are only used for system/integration producers.
 */
export const appendApprovalEventBodySchema = z
  .object({
    type: approvalEventTypeSchema,
    fileId: z.string().min(1),
    versionId: z.string().min(1),
    ticketId: z.string().nullable().optional(),
    idempotencyKey: z.string().min(1),

    subjectUserId: z.string().nullable().optional(),
    suggestedRole: z.string().nullable().optional(),
    reasoning: z.string().nullable().optional(),
    confidence: z.number().min(0).max(1).nullable().optional(),
    basis: z.array(z.string()).optional(),

    position: reviewDecisionSchema.nullable().optional(),

    noteId: z.string().nullable().optional(),
    noteBody: z.string().optional(),
    noteScope: z
      .object({
        type: z.enum(["version", "comment", "region", "project"]),
        targetId: z.string().nullable().optional(),
        region: z
          .object({
            page: z.number(),
            x: z.number(),
            y: z.number(),
            w: z.number(),
            h: z.number(),
          })
          .nullable()
          .optional(),
      })
      .optional(),
    parentNoteId: z.string().nullable().optional(),

    source: approvalEventSourceSchema.optional(),
    supersedesEventId: z.string().nullable().optional(),

    // Integration/system only — ignored for kind=user JWT callers.
    actorKind: approvalActorKindSchema.optional(),
    actorIntegration: z.enum(["wrike", "asana", "email"]).nullable().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.type === "position_recorded" && body.position == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "position is required for position_recorded",
        path: ["position"],
      });
    }
    if (
      body.type !== "position_recorded" &&
      body.position != null &&
      body.type !== "position_withdrawn"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "position is only valid on position_recorded",
        path: ["position"],
      });
    }
    if (body.type === "note_added" && !body.noteId && !body.noteBody) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "note_added requires noteId or noteBody",
        path: ["noteBody"],
      });
    }
  });

export const appendApprovalEventRequestSchema = z.object({
  params: z.object({
    projectId: z.string(),
    versionId: z.string(),
  }),
  body: appendApprovalEventBodySchema,
});

export const appendApprovalEventResponseSchema = z.object({
  event: approvalEventSchema,
  participation: participationSnapshotSchema,
  duplicated: z.boolean(),
});
export type AppendApprovalEventResponse = z.infer<
  typeof appendApprovalEventResponseSchema
>;

export const getApprovalParticipationRequestSchema = z.object({
  params: z.object({
    projectId: z.string(),
    versionId: z.string(),
  }),
  query: z
    .object({
      fileId: z.string().optional(),
    })
    .optional(),
});

/** Lightweight note shape on the participation GET (avoids circular import with approvalNote). */
export const approvalNoteRefSchema = z.object({
  noteId: z.string(),
  authorId: z.string().nullable(),
  authorKind: z.enum(["user", "agent"]),
  body: z.string(),
  parentNoteId: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type ApprovalNoteRef = z.infer<typeof approvalNoteRefSchema>;

export const getApprovalParticipationResponseSchema = z.object({
  participation: participationSnapshotSchema,
  events: z.array(approvalEventSchema),
  /** Active notes for the version (soft-deleted excluded by the handler). */
  notes: z.array(approvalNoteRefSchema).default([]),
});
export type GetApprovalParticipationResponse = z.infer<
  typeof getApprovalParticipationResponseSchema
>;
